import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Grid } from "@mui/material";
import { ListPagination } from "components/tables/list";
import { Loading, TooltipAdd } from "components/common";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Request from "../../../../core/requests";
import _ from "lodash";
import MainCard from "components/common/card/MainCard";
import StationsTableComponent from "components/tables/stations/StationsTable.component";
import AddStationContainer from "../add/AddStation.container";
import UpdateStationContainer from "../update/UpdateStation.container";
import { openSnackbar } from "store/slices/snackbar";
import { saveCluster, saveSearch } from "store/user.store";
import { refreshToken } from "store/user.store";
import { logout } from "store/user.store";
import ToolbarTableComponent from "components/tables/toolbar/ToolbarTable.component";
import { useTranslation } from "react-i18next";

const SUPERUSER = "superuser";
const OPERATOR = "operator";

const optionsChecked = {
  GATEWAY: false,
  ENVSENSOR: false,
  CAMERA: false,
  PCOUNTER: false,
  WIFI: false,
};

const StationsListContainer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    token,
    user,
    cluster,
    search,
    loading: loadingRefresh,
    error: errorRefresh,
  } = useSelector((state) => state.user);
  const [slice, setSlice] = useState(25);
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingZone, setLoadingZone] = useState(true);
  const [dataFilter, setDataFilter] = useState([]);
  const [isSuperUser, setIsSuperUser] = useState(false);
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [station, setStation] = useState(null);
  const [update, setUpdate] = useState(null);
  const [checked, setChecked] = useState(optionsChecked);
  const [values, setValues] = useState(null);
  const [listClusters, setListClusters] = useState([]);
  const [zoneSelected, setZoneSelected] = useState(cluster ? cluster : []);
  const [allStations, setAllStations] = useState(null);
  const [isFirstRefresh, setIsFirstRefresh] = useState(true);
  const [isOperator, setOperator] = useState(false);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("stationId");
  const [valueSearch, setValueSearch] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [idDelete, setIdDelete] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [isChageStatusSensor, setIsChageStatusSensor] = useState(null);

  const TABLE_HEAD = useMemo(
    () => [
      { id: "accordion", label: "", alignRight: false, order: true },
      { id: "stationId", label: "ID", alignRight: false, order: true },
      { id: "address", label: t("Address"), alignRight: false, order: true },
      { id: "cluster", label: "Cluster", alignRight: false, order: true },
      { id: "alias", label: "Alias", alignRight: false, order: true },
    ],
    [t]
  );

  const TABLE_HEAD_SUPERUSER = useMemo(
    () =>
      _.concat(
        TABLE_HEAD,
        { id: "edit", label: "", alignRight: false, order: true },
        { id: "delete", label: "", alignRight: false, order: true }
      ),
    [TABLE_HEAD]
  );
  const TABLE_HEAD_OPERATOR = useMemo(
    () =>
      _.concat(TABLE_HEAD, {
        id: "edit",
        label: "",
        alignRight: false,
        order: true,
      }),
    [TABLE_HEAD]
  );

  const handleOpen = useCallback((item) => {
    setIsChageStatusSensor({ id: item });
    setOpenModal(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpenModal(false);
  }, []);

  const onClickFilter = useCallback(
    (item) => {
      setChecked({ ...checked, [item]: !checked[item] });
      setPage(0);
    },
    [checked]
  );

  const handleChangeZone = useCallback((item) => {
    setZoneSelected(item);
    dispatch(saveCluster(item));
    setDataFilter([]);
    setPage(0);
    // eslint-disable-next-line
  }, []);

  const handleClickOpenDialog = useCallback(() => {
    setOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setOpen(false);
    setOpenEdit(false);
  }, []);

  const handleClickId = useCallback((id) => {
    navigate(`/singleStation/${id}`);
    // eslint-disable-next-line
  }, []);

  const handleRowEditClick = useCallback(
    (item) => {
      if (isSuperUser || isOperator) {
        setStation(item);
        setOpenEdit(true);
      }
    },
    // eslint-disable-next-line
    [isSuperUser, isOperator]
  );

  const handleDeletePCount = useCallback(
    (data) => {
      setUpdate(null);
      if (isSuperUser && !loadingRefresh && isChageStatusSensor === null) {
        setIdDelete(data);

        Request.stations
          .deleteStationPeopleCounter({
            token: token,
            ...data,
          })
          .then((resp) => {
            onSuccess(resp);
          })
          .catch((error) => onError(error, data));
      }
    },
    // eslint-disable-next-line
    [isSuperUser, token, loadingRefresh, isChageStatusSensor]
  );

  const handleDeletesensor = useCallback(
    (data) => {
      setUpdate(null);
      if (isSuperUser && !loadingRefresh && isChageStatusSensor === null) {
        setIdDelete(data);

        Request.stations
          .deleteSensorOfStation({
            token: token,
            ...data,
          })
          .then((resp) => {
            onSuccess(resp);
          })
          .catch((error) => onError(error, data));
      }
    },
    // eslint-disable-next-line
    [isSuperUser, token, loadingRefresh, isChageStatusSensor]
  );

  const handleRowDeleteClick = useCallback(
    (data) => {
      if (isSuperUser && !loadingRefresh && isChageStatusSensor === null) {
        setUpdate(null);
        setIdDelete(data);

        Request.stations
          .deleteStationDetail({ token: token, ...data })
          .then((resp) => onSuccess(resp))
          .catch((error) => onError(error, data));
      }
    },
    // eslint-disable-next-line
    [isSuperUser, token, loadingRefresh, isChageStatusSensor]
  );

  const onSuccess = useCallback((resp) => {
    setUpdate(resp);
    setIdDelete(null);
    setIsFirstRefresh(true);
    dispatch(
      openSnackbar({
        open: true,
        message: resp.message,
        variant: "alert",
        alert: {
          color: "success",
        },
        close: false,
      })
    );
    // eslint-disable-next-line
  }, []);

  const onFailure = useCallback((error) => {
    dispatch(
      openSnackbar({
        open: true,
        message: error.response?.data?.message
          ? error.response.data.message
          : "Errore",

        variant: "alert",
        alert: {
          color: "error",
        },
        close: false,
      })
    );
    // eslint-disable-next-line
  }, []);

  const onPageChange = useCallback((event, newPage) => {
    setPage(newPage);
    // eslint-disable-next-line
  }, []);

  const onRowsPerPageChange = useCallback((event) => {
    setSlice(parseInt(event.target.value));
    setPage(0);
    // eslint-disable-next-line
  }, []);

  const handleSearch = useCallback(
    (e) => {
      const searchValue = e.target.value;
      setValueSearch(searchValue);
      setPage(0);
      const searchResult = values?.stations?.filter((item) => {
        return (
          item.stationId?.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.tag?.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.alias?.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.address?.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.cluster?.toLowerCase().includes(searchValue.toLowerCase())
        );
      });
      setCount(searchResult?.length);
      setDataFilter(searchResult);
    },
    // eslint-disable-next-line
    [values]
  );

  const onError = useCallback(
    (error, data) => {
      if (isFirstRefresh) {
        setLoading(false);
        if (error?.response?.status === 403) {
          dispatch(logout());
        } else {
          if (error?.response?.status !== 401) {
            onFailure(error);
          } else {
            if (isFirstRefresh) {
              dispatch(
                refreshToken({
                  grant_type: "refresh_token",
                  refresh_token: user.refresh_token,
                })
              );
              setIsFirstRefresh(false);
            }
          }
        }
      } else {
        onLogout(error);
      }
    },
    // eslint-disable-next-line
    [isFirstRefresh]
  );

  const onLogout = useCallback((error) => {
    if (error?.response?.status === 403 || error?.response?.status === 401) {
      dispatch(logout());
    }
    // eslint-disable-next-line
  }, []);

  const onRequestSort = useCallback(
    (event, property) => {
      const isAsc = order === "asc" ? "desc" : "asc";
      setOrder(isAsc);
      setOrderBy(property);
    },
    [order]
  );

  const onSuccessStations = useCallback(
    (resp) => {
      setValues(resp);
      setLoading(false);
      setCount(resp?.stations?.length);
      setIsFirstRefresh(true);
      if (search) {
        setValueSearch(search);
        dispatch(saveSearch(null));
      } else {
        if (valueSearch === "") {
          setDataFilter(resp.stations);
        }
      }
    },
    // eslint-disable-next-line
    [search, valueSearch]
  );

  const onRefresh = useCallback(() => {
    setRefresh(!refresh);
  }, [refresh]);

  useEffect(() => {
    if (!loadingRefresh && isChageStatusSensor === null) {
      setLoadingZone(true);
      Request.stations
        .allPoi({
          token: token,
        })
        .then((resp) => {
          setAllStations(resp);
          setLoadingZone(false);
          setIsFirstRefresh(true);
        })
        .catch((error) => onError(error));
    }
    // eslint-disable-next-line
  }, [token, refresh, loadingRefresh, isChageStatusSensor]);

  useEffect(() => {
    //Ottengo dalla lista delle stazioni i cluster esistenti
    if (allStations && !_.isEmpty(allStations.stations)) {
      const clusters = allStations.stations.filter(
        (value, index, self) =>
          index === self.findIndex((t) => t.cluster === value.cluster)
      );
      const list = clusters.map((item) => {
        return item["cluster"];
      });

      setListClusters(list.sort());
    }
  }, [allStations]);

  useEffect(() => {
    if (!loadingRefresh && isChageStatusSensor === null) {
      setLoading(true);
      const envPath = checked["ENVSENSOR"] ? "&env_monitoring=true" : "";
      Request.stations
        .all({
          cluster: !_.isEmpty(zoneSelected) ? `cluster=${zoneSelected}&` : "",
          token: token,
          camera: checked["CAMERA"] ? true : "None",
          gateway: checked["GATEWAY"] ? true : "None",
          people_counter: checked["PCOUNTER"] ? true : "None",
          wifi: checked["WIFI"] ? true : "None",
          envPath: envPath,
        })
        .then((resp) => {
          onSuccessStations(resp);
        })
        .catch((error) => onError(error));
    }
    // eslint-disable-next-line
  }, [
    token,
    update,
    token,
    zoneSelected,
    checked,
    refresh,
    loadingRefresh,
    isChageStatusSensor,
  ]);

  useEffect(() => {
    if (valueSearch && !loading) {
      handleSearch({ target: { value: valueSearch } });
    }
    // eslint-disable-next-line
  }, [valueSearch, zoneSelected, loading, update]);

  useEffect(() => {
    if (user?.role === SUPERUSER) {
      setIsSuperUser(true);
    } else {
      if (user?.role === OPERATOR) {
        setOperator(true);
      } else {
        navigate("/404");
      }
    }
    // eslint-disable-next-line
  }, [user]);

  useEffect(() => {
    if (!isFirstRefresh && idDelete && !loadingRefresh) {
      if (idDelete?.station_id && idDelete?.sensor_id) {
        handleDeletesensor(idDelete);
      } else {
        if (idDelete?.station_id && idDelete?.pcounter_id !== null) {
          handleDeletePCount(idDelete);
        } else {
          handleRowDeleteClick(idDelete);
        }

      }
    }
    // eslint-disable-next-line
  }, [isFirstRefresh, idDelete, loadingRefresh, errorRefresh]);

  return (
    <Fragment>
      {loadingZone ? (
        <Loading />
      ) : (
        <Fragment>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ToolbarTableComponent
                listClusters={listClusters}
                handleChangeZone={handleChangeZone}
                loading={loadingZone}
                zoneSelected={zoneSelected}
                handleSearch={handleSearch}
                checked={checked}
                onClickFilter={onClickFilter}
                labelSearch={t("Search")}
                valueSearch={valueSearch}
                onRefresh={onRefresh}
              />
            </Grid>
            <Grid item xs={12}>
              <MainCard
                title={t("Monitoring points list")}
                content={false}
                secondary={
                  isSuperUser && (
                    <TooltipAdd
                      title={t("Add monitoring point")}
                      open={open}
                      handleClickOpenDialog={handleClickOpenDialog}
                    >
                      <AddStationContainer
                        setSuccess={setUpdate}
                        open={open}
                        handleCloseDialog={handleCloseDialog}
                      />
                    </TooltipAdd>
                  )
                }
              >
                {loading ? (
                  <Grid item xs={12}>
                    <Loading />
                  </Grid>
                ) : (
                  <Fragment>
                    <StationsTableComponent
                      header={
                        isSuperUser
                          ? TABLE_HEAD_SUPERUSER
                          : isOperator
                            ? TABLE_HEAD_OPERATOR
                            : TABLE_HEAD
                      }
                      data={dataFilter}
                      handleRowEditClick={handleRowEditClick}
                      handleRowDeleteClick={handleRowDeleteClick}
                      handleClickId={handleClickId}
                      page={page}
                      rowsPerPage={slice}
                      isSuperUser={isSuperUser}
                      setUpdate={setUpdate}
                      handleDeletesensor={handleDeletesensor}
                      isOperator={isOperator}
                      onRequestSort={onRequestSort}
                      order={order}
                      orderBy={orderBy}
                      handleOpen={handleOpen}
                      handleClose={handleClose}
                      openModal={openModal}
                      setIsChageStatusSensor={setIsChageStatusSensor}
                      isChageStatusSensor={isChageStatusSensor}
                      handleDeletePCount={handleDeletePCount}
                    />

                    <ListPagination
                      rowsPerPageOptions={[10, 25, 50, 100, 250]}
                      component="div"
                      count={count}
                      rowsPerPage={slice}
                      page={page}
                      onPageChange={onPageChange}
                      onRowsPerPageChange={onRowsPerPageChange}
                    />
                  </Fragment>
                )}
              </MainCard>
            </Grid>
          </Grid>

          {openEdit && (
            <UpdateStationContainer
              open={openEdit}
              station={station}
              handleCloseDialog={handleCloseDialog}
              setUpdate={setUpdate}
            />
          )}
        </Fragment>
      )}
    </Fragment>
  );
};

export default StationsListContainer;
